import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type UserRole = "superadmin" | "teamleider" | "accountmanager" | "recruiter" | null;

/**
 * Robust role/profile hooks that automatically refresh when the authenticated user changes.
 * - Query keys include userId to avoid cross-user caching
 * - Auth state listener invalidates caches on SIGNED_IN/SIGNED_OUT
 */
export const useUserRole = () => {
  const qc = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Initialize userId
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (mounted) setUserId(user?.id ?? null);
      if (mounted) setAuthChecked(true);
    });

    // Keep queries in sync with auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUserId = session?.user?.id ?? null;
      setUserId(nextUserId);
      // Invalidate cached role/profile for safety
      qc.invalidateQueries({ queryKey: ["user-role"] });
      qc.invalidateQueries({ queryKey: ["user-profile"] });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [qc]);

  const query = useQuery({
    queryKey: ["user-role", userId],
    enabled: userId !== null,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // SECURITY: Check app_metadata for superadmin role (set via Supabase Auth)
      const metadataRole = user.app_metadata?.role;
      if (metadataRole === 'superadmin') {
        return "superadmin" as UserRole;
      }

      // For all other users, fetch their role from user_roles table
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) throw error;
      const roles = (data as { role: string }[]) || [];

      // Return the highest priority role
      const priority = ["superadmin", "teamleider", "accountmanager", "recruiter"];
      const selected =
        roles.sort((a, b) => priority.indexOf(a.role) - priority.indexOf(b.role))[0]?.role || null;
      return selected as UserRole;
    },
  });

  return { ...query, isLoading: !authChecked || query.isLoading };
};

export const useUserProfile = () => {
  const qc = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (mounted) setUserId(user?.id ?? null);
      if (mounted) setAuthChecked(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
      qc.invalidateQueries({ queryKey: ["user-profile"] });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [qc]);

  const query = useQuery({
    queryKey: ["user-profile", userId],
    enabled: userId !== null,
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  return { ...query, isLoading: !authChecked || query.isLoading };
};
